import React, { useState, useEffect } from "react";
import { Download, Smartphone, X, Sparkles, Check, Share, PlusSquare, ArrowRight, ExternalLink } from "lucide-react";

export default function InstallAppBanner({ lang = "nl" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // 📱 Strict OS Detection
  const [deviceOS, setDeviceOS] = useState("unknown"); // 'ios' | 'android' | 'desktop'

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10000);
    // Check if already in standalone app mode
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      window.location.search.includes("app=1") ||
      window.location.hash.includes("app=1")
    ) {
      setIsInstalled(true);
      return;
    }

    // Detect user OS strictly
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    if (isIOS) {
      setDeviceOS("ios");
    } else if (isAndroid) {
      setDeviceOS("android");
    } else {
      setDeviceOS("desktop");
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsOpen(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deviceOS === "android" && deferredPrompt) {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult && choiceResult.outcome === "accepted") {
        setDeferredPrompt(null);
        setIsOpen(false);
        return;
      }
    }
    setShowModal(true);
  };

  if (isInstalled) return null;

  return (
    <>
      {/* 🚀 Floating Bottom Smart Banner */}
      {isOpen && isVisible && (
        <div className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-md z-40 animate-bounce-in">
          <div className="p-3.5 sm:p-4 rounded-3xl bg-slate-950/98 border-2 border-amber-500/70 shadow-2xl backdrop-blur-xl text-white flex items-center justify-between gap-3 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 w-28 h-28 bg-amber-500/15 rounded-full blur-2xl pointer-events-none" />

            <div className="flex items-center gap-3 min-w-0">
              {/* OS Specific Icon */}
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-slate-950 font-black shadow-lg shadow-amber-500/30 shrink-0">
                {deviceOS === "ios" ? (
                  <span className="text-xl">🍏</span>
                ) : deviceOS === "android" ? (
                  <span className="text-xl">🤖</span>
                ) : (
                  <Smartphone className="w-5 h-5 stroke-[2.5]" />
                )}
              </div>

              <div className="pr-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="font-serif font-black text-xs sm:text-sm text-white truncate">
                    {lang === "nl" ? "Aura Hair Afspraken" : lang === "tr" ? "Aura Hair Randevu" : "Aura Hair Booking"}
                  </h4>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-black uppercase ${
                    deviceOS === "ios"
                      ? "bg-sky-500/20 text-sky-300 border border-sky-400/40"
                      : "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                  }`}>
                    {deviceOS === "ios" ? "iOS / iPhone" : deviceOS === "android" ? "Android" : (lang === "nl" ? "Mobiele App" : lang === "tr" ? "Mobil App" : "Mobile App")}
                  </span>
                </div>
                <p className="text-[11px] text-amber-300/90 font-medium truncate mt-0.5">
                  {lang === "nl"
                    ? "Alleen afspraken • Snelle start"
                    : lang === "tr"
                    ? "Sadece randevu bölümü • Hızlı kurulum"
                    : "Only booking module • Instant app"}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={handleInstallClick}
                className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 hover:from-amber-300 hover:to-amber-400 text-slate-950 font-black text-xs transition-all cursor-pointer shadow-md shadow-amber-500/30 flex items-center gap-1.5 hover:scale-105 active:scale-95"
              >
                <Download className="w-3.5 h-3.5 stroke-[3]" />
                <span>
                  {lang === "nl" ? "Installeer" : lang === "tr" ? "İndir" : "Install"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                title={lang === "nl" ? "Sluiten" : lang === "tr" ? "Kapat" : "Close"}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 📱 STRICT OS INSTALLATION MODAL */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fade-in text-white"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border-2 border-amber-500/60 p-5 sm:p-7 rounded-3xl w-full max-w-md space-y-4 shadow-2xl relative my-auto text-left"
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/40 flex items-center justify-center font-black text-xl">
                  {deviceOS === "ios" ? "🍏" : "🤖"}
                </div>
                <div>
                  <h3 className="font-serif text-base sm:text-lg font-black text-white">
                    {deviceOS === "ios"
                      ? (lang === "nl" ? "Aura Hair voor iPhone (iOS)" : lang === "tr" ? "iPhone (iOS) İçin Aura Hair" : "Aura Hair for iPhone (iOS)")
                      : (lang === "nl" ? "Aura Hair voor Android" : lang === "tr" ? "Android İçin Aura Hair" : "Aura Hair for Android")}
                  </h3>
                  <p className="text-xs text-amber-400 font-medium">
                    {lang === "nl"
                      ? "Directe afspraakmodule op uw startscherm"
                      : lang === "tr"
                      ? "Ana ekrana doğrudan randevu uygulaması kurulur"
                      : "Direct booking module on your home screen"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 🍏 ONLY IOS CONTENT */}
            {deviceOS === "ios" && (
              <div className="space-y-3 text-xs text-slate-300">
                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-xl bg-amber-500 text-slate-950 font-black flex items-center justify-center shrink-0 text-xs">
                    1
                  </div>
                  <div>
                    <strong className="text-white block text-sm mb-0.5 flex items-center gap-1.5">
                      <span>{lang === "nl" ? "Tik op Delen" : lang === "tr" ? "Paylaş Simgesine Dokunun" : "Tap Share"}</span>
                      <Share className="w-3.5 h-3.5 text-sky-400" />
                    </strong>
                    <span>
                      {lang === "nl"
                        ? "Tik onderaan in Safari op het 'Delen'-icoon (het vierkantje met het pijltje omhoog ⬆️)."
                        : lang === "tr"
                        ? "Safari tarayıcınızın alt çubuğundaki 'Paylaş' simgesine (kare içinde yukarı ok ⬆️) dokunun."
                        : "Tap the Share icon at the bottom of Safari (square with up arrow ⬆️)."}
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-xl bg-emerald-500 text-slate-950 font-black flex items-center justify-center shrink-0 text-xs">
                    2
                  </div>
                  <div>
                    <strong className="text-white block text-sm mb-0.5 flex items-center gap-1.5">
                      <span>{lang === "nl" ? "Zet op beginscherm" : lang === "tr" ? "'Ana Ekrana Ekle'yi Seçin" : "Select 'Add to Home Screen'"}</span>
                      <PlusSquare className="w-3.5 h-3.5 text-emerald-400" />
                    </strong>
                    <span>
                      {lang === "nl"
                        ? "Scroll een stukje omlaag en kies 'Zet op beginscherm' (Add to Home Screen)."
                        : lang === "tr"
                        ? "Menüyü kaydırıp 'Ana Ekrana Ekle' (Add to Home Screen) seçeneğine tıklayın."
                        : "Scroll down and tap 'Add to Home Screen'."}
                    </span>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2 text-amber-300">
                  <Sparkles className="w-4 h-4 shrink-0 text-amber-400" />
                  <span className="text-[11px] leading-relaxed">
                    {lang === "nl"
                      ? "Klaar! Het icoon opent voortaan direct het snelle afsprakensysteem zonder de website."
                      : lang === "tr"
                      ? "İşte bu kadar! Telefonunuzdaki Aura Hair simgesi bundan sonra siteyi değil, doğrudan sadece randevu verme ekranını açar."
                      : "Done! The home screen icon now launches directly into the booking system."}
                  </span>
                </div>
              </div>
            )}

            {/* 🤖 ONLY ANDROID CONTENT */}
            {deviceOS === "android" && (
              <div className="space-y-3 text-xs text-slate-300">
                {/* Option A: Direct Install */}
                {deferredPrompt ? (
                  <button
                    type="button"
                    onClick={async () => {
                      deferredPrompt.prompt();
                      const choice = await deferredPrompt.userChoice;
                      if (choice?.outcome === "accepted") {
                        setDeferredPrompt(null);
                        setShowModal(false);
                        setIsOpen(false);
                      }
                    }}
                    className="w-full p-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 cursor-pointer hover:scale-102 transition-transform"
                  >
                    <Download className="w-4 h-4 stroke-[3]" />
                    <span>{lang === "nl" ? "⚡ Nu direct installeren op Android" : lang === "tr" ? "⚡ Şimdi Doğrudan Android'e Yükle" : "⚡ Install Directly on Android"}</span>
                  </button>
                ) : (
                  <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-xl bg-amber-500 text-slate-950 font-black flex items-center justify-center shrink-0 text-xs">
                      1
                    </div>
                    <div>
                      <strong className="text-white block text-sm mb-0.5">
                        {lang === "nl" ? "Chrome Menu (⋮)" : lang === "tr" ? "Chrome Menüsünü Açın (⋮)" : "Open Chrome Menu (⋮)"}
                      </strong>
                      <span>
                        {lang === "nl"
                          ? "Tik rechtsboven op de drie puntjes (⋮) en kies 'App installeren' of 'Toevoegen aan startscherm'."
                          : lang === "tr"
                          ? "Chrome'un sağ üstündeki 3 noktaya (⋮) dokunun ve 'Uygulamayı yükle' veya 'Ana ekrana ekle'yi seçin."
                          : "Tap the 3 dots (⋮) in the top right of Chrome and select 'Install app' or 'Add to Home screen'."}
                      </span>
                    </div>
                  </div>
                )}

                {/* Option B: Google Play Console link */}
                <a
                  href="https://play.google.com/store/apps/details?id=nl.aurahair.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full p-3.5 rounded-2xl bg-slate-950 hover:bg-slate-850 border border-amber-500/40 text-white flex items-center justify-between gap-3 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl">▶️</span>
                    <div>
                      <span className="text-[10px] text-slate-400 uppercase font-bold block">Google Play Store</span>
                      <strong className="text-white text-xs group-hover:text-amber-400 transition-colors">
                        {lang === "nl" ? "Aura Hair Afspraken (Google Play)" : lang === "tr" ? "Aura Hair Randevu (Google Play)" : "Aura Hair Booking (Google Play)"}
                      </strong>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-amber-400" />
                </a>

                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center gap-2 text-amber-300">
                  <Sparkles className="w-4 h-4 shrink-0 text-amber-400" />
                  <span className="text-[11px] leading-relaxed">
                    {lang === "nl"
                      ? "Opent direct als standalone app met uitsluitend de boekingsmodule!"
                      : lang === "tr"
                      ? "Ana ekrana kurulduğunda web sitesini değil, doğrudan sadece randevu ekranını açar!"
                      : "Opens directly as a standalone app with only the booking module!"}
                  </span>
                </div>
              </div>
            )}

            {/* 💻 DESKTOP FALLBACK */}
            {deviceOS === "desktop" && (
              <div className="space-y-3 text-xs text-slate-300">
                <p className="leading-relaxed">
                  {lang === "nl"
                    ? "Open rotterdamaurahair.com op uw iPhone of Android telefoon om de app direct te installeren."
                    : lang === "tr"
                    ? "Aura Hair Randevu uygulamasını yüklemek için bu siteyi iPhone veya Android telefonunuzdan ziyaret edin."
                    : "Visit rotterdamaurahair.com on your iPhone or Android phone to install the app directly."}
                </p>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-center">
                    <span className="text-2xl block mb-1">🍏</span>
                    <strong className="text-white text-xs block">iPhone (iOS)</strong>
                    <span className="text-[10px] text-slate-400">
                      {lang === "nl" ? "Safari • Zet op beginscherm" : lang === "tr" ? "Safari • Ana Ekrana Ekle" : "Safari • Add to Home Screen"}
                    </span>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 text-center">
                    <span className="text-2xl block mb-1">🤖</span>
                    <strong className="text-white text-xs block">Android</strong>
                    <span className="text-[10px] text-slate-400">Google Play &bull; PWA</span>
                  </div>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg shadow-amber-500/25"
            >
              {lang === "nl" ? "Sluiten" : lang === "tr" ? "Tamam, Anladım" : "Close"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}